import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  signInWithCustomToken,
  linkWithCredential,
  GoogleAuthProvider,
} from "firebase/auth";
import { OAuthProvider } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, googleProvider, functions } from "./firebase";
import { clearStoredIdToken, persistIdToken } from "./tokenUtils";

export const waitForAuthReady = () =>
  new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user || null);
    });
  });

export const getFreshIdToken = async (forceRefresh = false) => {
  const user = auth.currentUser || (await waitForAuthReady());
  if (!user) {
    return null;
  }
  const token = await user.getIdToken(forceRefresh);
  if (token) {
    persistIdToken(token);
  }
  return token;
};

export const signInWithGooglePopup = async (loginHint) => {
  if (loginHint) {
    googleProvider.setCustomParameters({ login_hint: loginHint });
  }
  return signInWithPopup(auth, googleProvider);
};

const handleManualWeld = async ({ email, providerId, pendingCred }) => {
  if (!pendingCred?.idToken) return null;
  const weldSSO = httpsCallable(functions, "manual_weld_sso");
  const result = await weldSSO({
    email,
    providerId,
    oauthToken: pendingCred.idToken,
  });
  const customToken = result?.data?.customToken;
  if (!customToken) return null;
  const userCred = await signInWithCustomToken(auth, customToken);
  try {
    await linkWithCredential(userCred.user, pendingCred);
  } catch {
    // Ignore link errors; user is already signed in via custom token.
  }
  return userCred;
};

export const signInWithGooglePopupSmart = async (loginHint, options = {}) => {
  const { prompt } = options || {};
  if (loginHint || prompt) {
    googleProvider.setCustomParameters({
      ...(loginHint ? { login_hint: loginHint } : {}),
      ...(prompt ? { prompt } : {}),
    });
  }
  try {
    return await signInWithPopup(auth, googleProvider);
  } catch (error) {
    if (error?.code === "auth/account-exists-with-different-credential") {
      const pendingCred = GoogleAuthProvider.credentialFromError(error);
      return handleManualWeld({
        email: error?.customData?.email,
        providerId: "google.com",
        pendingCred,
      });
    }
    throw error;
  }
};

export const signInWithGoogleRedirect = async (loginHint) => {
  if (loginHint) {
    googleProvider.setCustomParameters({ login_hint: loginHint });
  }
  return signInWithRedirect(auth, googleProvider);
};

export const signInWithMicrosoftPopup = async (loginHint) => {
  const provider = new OAuthProvider("microsoft.com");
  if (loginHint) {
    provider.setCustomParameters({ login_hint: loginHint });
  }
  return signInWithPopup(auth, provider);
};

export const signInWithMicrosoftPopupSmart = async (loginHint, options = {}) => {
  const provider = new OAuthProvider("microsoft.com");
  const { prompt } = options || {};
  if (loginHint || prompt) {
    provider.setCustomParameters({
      ...(loginHint ? { login_hint: loginHint } : {}),
      ...(prompt ? { prompt } : {}),
    });
  }
  try {
    return await signInWithPopup(auth, provider);
  } catch (error) {
    if (error?.code === "auth/account-exists-with-different-credential") {
      const pendingCred = OAuthProvider.credentialFromError(error);
      return handleManualWeld({
        email: error?.customData?.email,
        providerId: "microsoft.com",
        pendingCred,
      });
    }
    throw error;
  }
};

export const signInWithMicrosoftRedirect = async (loginHint) => {
  const provider = new OAuthProvider("microsoft.com");
  if (loginHint) {
    provider.setCustomParameters({ login_hint: loginHint });
  }
  return signInWithRedirect(auth, provider);
};

export const exchangeSsoSession = async () => {
  const response = await fetch("/api/v1/sso/firebase-token", {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) return null;
  const { token } = await response.json();
  if (!token) return null;
  const userCredential = await signInWithCustomToken(auth, token);
  return userCredential.user;
};

// The handoff cookie is single-use — Login and ProtectedRoute can both mount
// around the same time, so they share one in-flight attempt instead of each
// racing to redeem it independently.
let ssoExchangePromise = null;
export const runSsoExchangeOnce = () => {
  if (!ssoExchangePromise) {
    ssoExchangePromise = exchangeSsoSession().catch(() => null);
  }
  return ssoExchangePromise;
};

export const handleGoogleRedirectResult = () => getRedirectResult(auth);

// Cached profile and routing state from the signed-in session. Left behind,
// "user" lets ProtectedRoute treat the browser as signed in (skipping the SSO
// exchange), and "lastLoginHint" pre-fills the next person's login.
const CACHED_SESSION_KEYS = [
  "user",
  "userName",
  "userId",
  "companyAssigned",
  "currentRole",
  "lastLoginHint",
];

export const firebaseSignOut = async () => {
  await signOut(auth);
  clearStoredIdToken();
  try {
    CACHED_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // localStorage might be unavailable
  }
};
